import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { WindowRef } from '@spartacus/core';
import { Subscription, asyncScheduler, fromEvent } from 'rxjs';
import { distinctUntilChanged, throttleTime } from 'rxjs/operators';

export enum Side {
  ABOVE = 'above',
  BELOW = 'below',
  ALWAYS = 'always',
}

/**
 * Следит за вертикальным скроллом и эмитит событие, когда элемент покидает/входит в область видимости.
 *
 * input ignoreSide - когда эмитить событие
 *  - below (default) - отслеживаемый за верхней границей
 *  - always - всегда
 *  - above - отслеживаемый за нижней границей
 *
 *
 * output isOutside - эмитится true, когда отслеживаемый элемент выходит за пределы видимости
 */
@Directive({
  selector: '[sdOutsideViewport]',
  exportAs: 'sdOutsideViewport',
  standalone: true,
})
export class OutsideViewportDirective implements OnDestroy, AfterViewInit {
  @Input('sdOutsideViewport') ignoreSide?: Side;
  @Output() isOutside = new EventEmitter<boolean>();
  private scrollSubscription = new Subscription();
  private throttleDelay = 100;

  constructor(
    private elemRef: ElementRef<HTMLElement>,
    private winRef: WindowRef,
  ) {
    if (!this.ignoreSide) {
      this.ignoreSide = Side.BELOW;
    }
  }

  ngAfterViewInit(): void {
    if (!this.winRef.nativeWindow) {
      return;
    }

    this.onViewportScroll();

    this.scrollSubscription = fromEvent(this.winRef.nativeWindow, 'scroll')
      .pipe(throttleTime(this.throttleDelay, asyncScheduler, { trailing: true }), distinctUntilChanged())
      .subscribe(() => {
        this.onViewportScroll();
      });
  }

  ngOnDestroy(): void {
    this.scrollSubscription.unsubscribe();
  }

  private calcVisibility(): boolean {
    const elementTop = this.elemRef.nativeElement.getBoundingClientRect().top;
    const windowBottom = this.winRef.nativeWindow?.innerHeight || 0;

    switch (this.ignoreSide) {
      case Side.ABOVE:
        return windowBottom < elementTop;
      case Side.BELOW:
        return elementTop < 0;
      case Side.ALWAYS:
        return windowBottom < elementTop || elementTop < 0;
    }

    return false;
  }

  private onViewportScroll(): void {
    this.isOutside.emit(this.calcVisibility());
  }
}
